// Pure string-template helper — interpolates `{key}` placeholders in a
// template against a record-data object. Client-side companion to the
// backend's `display_name_format` rendering for M2O / M2M picker labels.
//
// Why client-side: a `<field display_format="..." />` XML override is
// known only to the view DSL; the server-rendered `display_name` doesn't
// reflect it. The picker has the full data fields in each candidate
// payload, so the format string can be re-applied at render-time.
//
// Mirrors Python `str.format_map` semantics:
//   - `{key}` → look up `key` in `data`. Missing keys, `null`, `undefined`
//     render as the empty string (so "[{code}] {name}" gracefully
//     degrades to "[] Some Name" rather than throwing).
//   - `{{` and `}}` → literal `{` and `}`.
//   - All other characters pass through verbatim.
//   - Numeric / positional placeholders are NOT supported (mirrors the
//     backend decorator's documented "field-name keys only" contract).

export function formatDisplay(template: string, data: Record<string, unknown>): string {
    let out = "";
    let i = 0;
    const len = template.length;

    while (i < len) {
        const ch = template[i];

        if (ch === "{") {
            if (template[i + 1] === "{") {
                out += "{";
                i += 2;
                continue;
            }
            const close = template.indexOf("}", i + 1);
            if (close === -1) {
                out += template.slice(i);
                break;
            }
            const key = template.slice(i + 1, close).trim();
            if (!key) {
                out += template.slice(i, close + 1);
            } else {
                const head = key.split(".")[0].split("[")[0].trim();
                const value = data[head];
                out += value == null ? "" : String(value);
            }
            i = close + 1;
            continue;
        }

        if (ch === "}" && template[i + 1] === "}") {
            out += "}";
            i += 2;
            continue;
        }

        out += ch;
        i += 1;
    }

    return out;
}
